import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import UniversalMap from './maps/UniversalMap';
import { LatLng, UniversalMapEntry, RouteResult } from './maps/MapInterface';

interface Experience {
  id: string;
  day: number;
  title: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  type: string;
  time?: string;
}

interface RoutePoint {
  coords: LatLng;
  title: string;
  type: 'start' | 'end' | 'accommodation' | 'attraction';
  description: string;
}

interface RouteMapProps {
  startDestination?: string;
  endDestination?: string;
  height?: number;
  waypoints?: Experience[];
}

const RouteMap: React.FC<RouteMapProps> = ({ 
  startDestination, 
  endDestination, 
  height = 400,
  waypoints = []
}) => {
  const [mapEntries, setMapEntries] = useState<UniversalMapEntry[]>([]);
  const [routeData, setRouteData] = useState<any>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);

  // Center of US as default
  const defaultCenter: LatLng = { lat: 39.8283, lng: -98.5795 };

  // Color mapping for different experience types
  const getMarkerColor = (type: string) => {
    switch (type) {
      case 'attraction': return '#2196f3';
      case 'restaurant': return '#ff9800';
      case 'accommodation': return '#4caf50';
      case 'activity': return '#9c27b0';
      default: return '#607d8b';
    }
  };

  // Convert waypoints to UniversalMap entries
  useEffect(() => {
    if (waypoints.length === 0) {
      setMapEntries([]);
      return;
    }

    // Sort waypoints by day and time for proper route order
    const sortedWaypoints = [...waypoints].sort((a, b) => {
      // First sort by day
      if (a.day !== b.day) {
        return a.day - b.day;
      }
      // Then by time if both have time
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      // Put items with time before items without time
      if (a.time && !b.time) return -1;
      if (!a.time && b.time) return 1;
      // If neither has time, maintain original order
      return 0;
    });

    // Convert waypoints to Universal Map entries
    const entries: UniversalMapEntry[] = sortedWaypoints
      .filter(wp => wp.location && wp.location.lat && wp.location.lng)
      .map((waypoint, index) => ({
        id: waypoint.id,
        title: waypoint.title,
        location: { lat: waypoint.location!.lat, lng: waypoint.location!.lng },
        locationName: waypoint.location!.address,
        content: `Day ${waypoint.day} • ${waypoint.type}`,
        date: waypoint.time,
        waypointIndex: index + 1,
        experienceType: waypoint.type,
        markerColor: getMarkerColor(waypoint.type),
        // Add route context for the map component
        isRouteWaypoint: true,
        startDestination,
        endDestination,
        totalWaypoints: sortedWaypoints.length
      }));

    setMapEntries(entries);

    // TODO: Calculate route if start/end destinations are provided
    // This will require Mapbox Directions API integration
    if (startDestination && endDestination && entries.length > 0) {
      calculateRoute(startDestination, endDestination, entries);
    }
  }, [waypoints, startDestination, endDestination]);

  // Calculate route using Mapbox Directions API with accommodation-based routing
  const calculateRoute = async (start: string, end: string, waypoints: UniversalMapEntry[]) => {
    setIsCalculatingRoute(true);
    setRouteError(null);
    
    try {
      const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      if (!accessToken) {
        throw new Error('Mapbox access token not configured');
      }

      // Geocode start and end destinations if they're strings
      const startCoords = await geocodeLocation(start);
      const endCoords = await geocodeLocation(end);
      
      if (!startCoords || !endCoords) {
        throw new Error('Could not geocode start or end destination');
      }

      console.log('🗺️ Starting accommodation-based route calculation...');
      console.log('📍 Start:', start, startCoords);
      console.log('📍 End:', end, endCoords);
      console.log('🎯 Waypoints:', waypoints.map(wp => `${wp.title} (${wp.experienceType})`));

      // Create accommodation-based route sequence
      const routeSequence = buildAccommodationBasedRoute(startCoords, endCoords, waypoints);
      console.log('🛣️ Route sequence:', routeSequence.map(r => r.description));

      // Calculate the route geometry using the optimized sequence
      const routeGeometry = await calculateOptimizedRoute(routeSequence, accessToken);

      const routeData: RouteResult = {
        distance: 0, // We'll calculate this from segments if needed
        duration: 0, // We'll calculate this from segments if needed
        geometry: routeGeometry,
        waypoints: []
      };

      setRouteData(routeData);
      console.log('✅ Route calculated successfully with', routeGeometry.length, 'points');

      setIsCalculatingRoute(false);
    } catch (error) {
      console.error('Route calculation failed:', error);
      setRouteError(`Failed to calculate route: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsCalculatingRoute(false);
    }
  };

  // Build accommodation-based route sequence: Start → Hotel1 → Attraction → Hotel1 → Hotel2 → End
  const buildAccommodationBasedRoute = (start: LatLng, end: LatLng, waypoints: UniversalMapEntry[]): RoutePoint[] => {
    const accommodations = waypoints.filter(wp => wp.experienceType === 'accommodation');
    const nonAccommodations = waypoints.filter(wp => wp.experienceType !== 'accommodation');
    
    console.log('🏨 Accommodations:', accommodations.map(a => a.title));
    console.log('🎭 Non-accommodations:', nonAccommodations.map(n => n.title));

    const routeSequence: RoutePoint[] = [];

    // Add start point
    routeSequence.push({
      coords: start,
      title: 'Start',
      type: 'start',
      description: 'Journey Start'
    });

    // Track current accommodation for non-accommodation round trips
    let currentAccommodation: UniversalMapEntry | null = null;

    // Process all waypoints in chronological order
    const allWaypoints = [...waypoints].sort((a, b) => {
      // Sort by day first, then by time
      if (a.waypointIndex !== b.waypointIndex) {
        return (a.waypointIndex || 0) - (b.waypointIndex || 0);
      }
      return 0;
    });

    for (const waypoint of allWaypoints) {
      if (waypoint.experienceType === 'accommodation') {
        // Accommodation becomes a main waypoint
        currentAccommodation = waypoint;
        routeSequence.push({
          coords: waypoint.location,
          title: waypoint.title,
          type: 'accommodation',
          description: `Accommodation: ${waypoint.title}`
        });
        console.log(`🏨 Added accommodation waypoint: ${waypoint.title}`);
      } else {
        // Non-accommodation: round trip from last accommodation
        if (currentAccommodation) {
          // Go from current accommodation to attraction
          routeSequence.push({
            coords: waypoint.location,
            title: waypoint.title,
            type: 'attraction',
            description: `Visit: ${waypoint.title}`
          });
          
          // Return to accommodation (unless it's the last waypoint and we're going to end)
          const isLastWaypoint = waypoint === allWaypoints[allWaypoints.length - 1];
          const hasMoreAccommodations = allWaypoints.slice(allWaypoints.indexOf(waypoint) + 1)
            .some(wp => wp.experienceType === 'accommodation');
          
          if (!isLastWaypoint || hasMoreAccommodations) {
            routeSequence.push({
              coords: currentAccommodation.location,
              title: currentAccommodation.title,
              type: 'accommodation',
              description: `Return to: ${currentAccommodation.title}`
            });
            console.log(`🔄 Round trip: ${currentAccommodation.title} → ${waypoint.title} → ${currentAccommodation.title}`);
          } else {
            console.log(`🎯 Final attraction: ${waypoint.title} (no return trip)`);
          }
        } else {
          // No accommodation yet, treat as regular waypoint from start
          routeSequence.push({
            coords: waypoint.location,
            title: waypoint.title,
            type: 'attraction',
            description: `Visit: ${waypoint.title} (from start)`
          });
          console.log(`🎯 Added attraction from start: ${waypoint.title}`);
        }
      }
    }

    // Add end point
    routeSequence.push({
      coords: end,
      title: 'End',
      type: 'end',
      description: 'Journey End'
    });

    return routeSequence;
  };

  // Calculate route with the optimized accommodation-based sequence
  const calculateOptimizedRoute = async (routeSequence: RoutePoint[], accessToken: string): Promise<LatLng[]> => {
    if (routeSequence.length < 2) {
      return [];
    }

    const allGeometry: LatLng[] = [];

    // Calculate route segment by segment
    for (let i = 0; i < routeSequence.length - 1; i++) {
      const segmentStart = routeSequence[i];
      const segmentEnd = routeSequence[i + 1];
      
      console.log(`📍 Calculating segment: ${segmentStart.description} → ${segmentEnd.description}`);
      
      try {
        const segmentGeometry = await calculateDirectRoute(segmentStart.coords, segmentEnd.coords, accessToken);
        
        // Add segment geometry, avoiding duplicate points at connections
        if (i === 0) {
          allGeometry.push(...segmentGeometry);
        } else {
          // Skip the first point to avoid duplication
          allGeometry.push(...segmentGeometry.slice(1));
        }
        
        console.log(`✅ Segment calculated: ${segmentGeometry.length} points`);
      } catch (error) {
        console.warn(`⚠️ Failed to calculate segment ${i} to ${i + 1}, using direct line:`, error);
        // Fallback to straight line for this segment
        if (i === 0) {
          allGeometry.push(segmentStart.coords, segmentEnd.coords);
        } else {
          allGeometry.push(segmentEnd.coords);
        }
      }
    }

    console.log(`🛣️ Total route geometry: ${allGeometry.length} points`);
    return allGeometry;
  };

  // Calculate a direct route between two points
  const calculateDirectRoute = async (start: LatLng, end: LatLng, accessToken: string): Promise<LatLng[]> => {
    const coordinates = `${start.lng},${start.lat};${end.lng},${end.lat}`;
    const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?` +
      `geometries=geojson&overview=full&steps=false&access_token=${accessToken}`;

    const response = await fetch(directionsUrl);
    const data = await response.json();

    if (data.routes && data.routes.length > 0) {
      return data.routes[0].geometry.coordinates.map((coord: number[]) => ({
        lat: coord[1],
        lng: coord[0]
      }));
    }
    
    throw new Error('No route found for direct path');
  };

  // Calculate route with waypoints
  const calculateWaypointRoute = async (start: LatLng, end: LatLng, waypoints: UniversalMapEntry[], accessToken: string): Promise<LatLng[]> => {
    const waypointCoords = waypoints.map(wp => `${wp.location.lng},${wp.location.lat}`);
    const coordinates = [
      `${start.lng},${start.lat}`,
      ...waypointCoords,
      `${end.lng},${end.lat}`
    ].join(';');

    // Use simpler parameters to avoid routing issues
    const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?` +
      `geometries=geojson&overview=full&steps=false&access_token=${accessToken}`;

    console.log('Calculating waypoint route with URL:', directionsUrl);

    const response = await fetch(directionsUrl);
    const data = await response.json();

    if (data.routes && data.routes.length > 0) {
      return data.routes[0].geometry.coordinates.map((coord: number[]) => ({
        lat: coord[1],
        lng: coord[0]
      }));
    }
    
    throw new Error('No route found for waypoint path');
  };

  // Calculate route in segments to avoid routing oddities
  const calculateSegmentedRoute = async (start: LatLng, end: LatLng, waypoints: UniversalMapEntry[], accessToken: string): Promise<LatLng[]> => {
    const allPoints = [start, ...waypoints.map(wp => wp.location), end];
    const allGeometry: LatLng[] = [];

    // Calculate route segment by segment
    for (let i = 0; i < allPoints.length - 1; i++) {
      const segmentStart = allPoints[i];
      const segmentEnd = allPoints[i + 1];
      
      try {
        const segmentGeometry = await calculateDirectRoute(segmentStart, segmentEnd, accessToken);
        
        // Add segment geometry, avoiding duplicate points at connections
        if (i === 0) {
          allGeometry.push(...segmentGeometry);
        } else {
          // Skip the first point to avoid duplication
          allGeometry.push(...segmentGeometry.slice(1));
        }
      } catch (error) {
        console.warn(`Failed to calculate segment ${i} to ${i + 1}, using direct line`);
        // Fallback to straight line for this segment
        if (i === 0) {
          allGeometry.push(segmentStart, segmentEnd);
        } else {
          allGeometry.push(segmentEnd);
        }
      }
    }

    return allGeometry;
  };

  // Geocode a location string to coordinates
  const geocodeLocation = async (location: string): Promise<LatLng | null> => {
    try {
      const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json?access_token=${accessToken}&limit=1`;
      
      const response = await fetch(geocodeUrl);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return { lat, lng };
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding failed:', error);
      return null;
    }
  };

  const getCustomMarkerSvg = (waypointIndex: number, color: string) => {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="12" fill="${color}" stroke="#fff" stroke-width="3"/>
        <text x="16" y="20" text-anchor="middle" fill="#fff" font-size="12" font-weight="bold">${waypointIndex}</text>
      </svg>
    `)}`;
  };  return (
    <Box sx={{ position: 'relative', width: '100%', height: height }}>
      {(isCalculatingRoute) && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '20px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Typography variant="body2">Calculating route...</Typography>
        </Box>
      )}
      
      {routeError && (
        <Box sx={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          right: '10px',
          zIndex: 1000,
          backgroundColor: 'rgba(244, 67, 54, 0.9)',
          color: 'white',
          padding: '10px',
          borderRadius: '4px'
        }}>
          <Typography variant="body2">{routeError}</Typography>
        </Box>
      )}

      <UniversalMap
        center={defaultCenter}
        zoom={4}
        entries={mapEntries}
        route={routeData}
        showSearch={false}
        showControls={true}
        interactive={true}
        style={{ height: '100%', width: '100%' }}
      />
    </Box>
  );
};

export default RouteMap;
